import React from "react";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { ColorPicker, useColor } from "react-color-palette";
import "react-color-palette/css";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { badgesAddModalStore } from "../stores/badgesAddModal";
import { badgesStore } from "../stores/badges";

function BadgeColorField({ label, value, onChange }) {
	const [showPicker, setShowPicker] = React.useState(false);
	const normalizedValue = typeof value === "string" && value.trim() ? value : "#FFFFFF";
	const [color, setColor] = useColor(normalizedValue);
	const safeColorHex = typeof color?.hex === "string" && color.hex.trim() ? color.hex : "#FFFFFF";

	return (
		<>
			{showPicker && (
				<Modal
					visible={showPicker}
					onDismiss={() => setShowPicker(false)}
					header={label}
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="link" onClick={() => setShowPicker(false)}>
									Close
								</Button>
							</SpaceBetween>
						</Box>
					}>
					<ColorPicker
						color={color}
						onChange={(nextColor) => {
							setColor(nextColor);
							if (typeof nextColor?.hex === "string" && nextColor.hex.trim()) {
								onChange(nextColor.hex);
							}
						}}
					/>
				</Modal>
			)}
			<Box variant="span">{label}</Box>
			<SpaceBetween direction="horizontal" size="s">
				<div
					onClick={() => setShowPicker(true)}
					role="button"
					tabIndex={0}
					onKeyDown={(event) => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							setShowPicker(true);
						}
					}}
					style={{
						width: "30px",
						height: "30px",
						backgroundColor: safeColorHex,
						border: "1px solid #ccc",
						cursor: "pointer",
					}}
				/>
				<Box variant="small">{safeColorHex}</Box>
			</SpaceBetween>
		</>
	);
}

class BadgesAddModal extends React.Component {
	addBadge() {
		const name = String(this.props.badgesAddModal.name || "").trim();
		if (!name.length) {
			this.props.Interface.addNoty("Error", "Badge name is required");
			return;
		}
		if (name.toLowerCase() === "rtpup") {
			this.props.Interface.addNoty("Error", "Badge name rtpUp is reserved");
			return;
		}
		this.props.Interface
			.request(
				"get",
				{ class: "data", module: "badges", method: "add" },
				{
					name,
					text_color: this.props.badgesAddModal.text_color,
					background_color: this.props.badgesAddModal.background_color,
					status: this.props.badgesAddModal.status.value,
				}
			)
			.then(() => {
				this.props.setBadgesAddModal({ ...this.props.badgesAddModal, show: false });
				this.props.Interface.loadBadges();
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };
				this.props.setBadges({ ...this.props.badges, loaded: true, error });
			});
	}

	render() {
		return (
			<Modal
				visible={this.props.badgesAddModal.show}
				onDismiss={() => {
					this.props.setBadgesAddModal({ ...this.props.badgesAddModal, show: false });
				}}
				header="Add badge"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="s">
							<Button
								variant="normal"
								onClick={() => {
									this.props.setBadgesAddModal({ ...this.props.badgesAddModal, show: false });
								}}>
								Close
							</Button>
							<Button variant="primary" onClick={() => this.addBadge()}>
								Ok
							</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween direction="vertical" size="m">
					<Box variant="span">Name</Box>
					<Input
						autoFocus
						value={this.props.badgesAddModal.name}
						onChange={(input) => this.props.setBadgesAddModal({ ...this.props.badgesAddModal, name: input.detail.value })}
					/>
					<BadgeColorField
						label="Text color"
						value={this.props.badgesAddModal.text_color}
						onChange={(text_color) => this.props.setBadgesAddModal({ ...this.props.badgesAddModal, text_color })}
					/>
					<BadgeColorField
						label="Background color"
						value={this.props.badgesAddModal.background_color}
						onChange={(background_color) => this.props.setBadgesAddModal({ ...this.props.badgesAddModal, background_color })}
					/>
					<Box variant="span">Status</Box>
					<Select
						filteringType="auto"
						selectedOption={this.props.badgesAddModal.status}
						onChange={(option) => this.props.setBadgesAddModal({ ...this.props.badgesAddModal, status: option.detail.selectedOption })}
						options={this.props.badgesAddModal.currentOption.status}
					/>
				</SpaceBetween>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const badgesAddModal = useRecoilValue(badgesAddModalStore);
		const setBadgesAddModal = useSetRecoilState(badgesAddModalStore);
		const badges = useRecoilValue(badgesStore);
		const setBadges = useSetRecoilState(badgesStore);
		return <Component {...props} badgesAddModal={badgesAddModal} setBadgesAddModal={setBadgesAddModal} badges={badges} setBadges={setBadges} />;
	};
}

export default withRecoil(BadgesAddModal);
